/**
 * Lightweight Video Coordinator for the host side.
 * Handles MediaStream management and coordinates with worker-side VideoSystem.
 * 
 * CORRECT OffscreenCanvas Approach:
 * - Create OffscreenCanvas on host (no context!)
 * - Transfer to worker immediately 
 * - Worker gets context and handles all processing
 * - Send video frames as ImageBitmap for worker to draw
 */
export class VideoCoordinator {
  // Video elements for MediaStream processing
  private videoElement: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  // Note: currentStream was removed as it was unused
  
  // Transfer coordination
  private transferLoopId: number | null = null;
  private isTransferRunning = false;
  private lastTransferTime = 0;
  private transferInterval = 1000 / 30; // Transfer at 30 FPS
  
  // Video state (lightweight - main state is in worker)
  private coordinatorState = {
    isConnected: false,
    sourceType: '',
    frameWidth: 0,
    frameHeight: 0
  };
  
  // Track if OffscreenCanvas has been sent to worker
  private hasTransferredCanvas = false;
  
  // Callback to send data to worker
  private sendToWorker: ((data: any, transfer?: Transferable[]) => void) | null = null;
  
  // Debug logging control
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }

  constructor(sendToWorkerCallback?: (data: any, transfer?: Transferable[]) => void) {
    // Bind methods for proper context
    this.handleVideoStreamUpdate = this.handleVideoStreamUpdate.bind(this);
    this.transferVideoFrame = this.transferVideoFrame.bind(this);
    this.sendToWorker = sendToWorkerCallback || null;
  }

  /**
   * Get the current video coordinator state (for host-side usage)
   */
  public getCoordinatorState() {
    return { ...this.coordinatorState };
  }

  /**
   * Handle video stream update (called from VijiCore)
   */
  public handleVideoStreamUpdate(data: {
    videoStream?: MediaStream | null;
    targetFrameRate?: number;
    cvConfig?: any; // Phase 11 preparation
    timestamp: number;
  }): void {
    try {
      if (data.videoStream) {
        this.setVideoStream(data.videoStream);
      } else {
        // Stream removed or null
        this.disconnectVideoStream();
      }
      
      // Forward configuration to worker
      if (data.targetFrameRate || data.cvConfig) {
        this.sendConfigurationToWorker({
          ...(data.targetFrameRate && { targetFrameRate: data.targetFrameRate }),
          ...(data.cvConfig && { cvConfig: data.cvConfig }),
          timestamp: data.timestamp
        });
      }
    } catch (error) {
      console.error('Error handling video stream update:', error);
      this.coordinatorState.isConnected = false;
      this.sendDisconnectionToWorker();
    }
  }

  /**
   * Set the video stream for processing
   */
  private async setVideoStream(videoStream: MediaStream): Promise<void> {
    // Clean up existing connections
    this.disconnectVideoStream();
    
    // Check if stream has video tracks
    const videoTracks = videoStream.getVideoTracks();
    if (videoTracks.length === 0) {
      console.warn('No video tracks in provided stream');
      this.coordinatorState.isConnected = false;
      this.sendDisconnectionToWorker();
      return;
    }
    
    try {
      // Create video element for stream playback
      this.videoElement = document.createElement('video');
      this.videoElement.autoplay = true;
      this.videoElement.muted = true; // Prevent audio from playing
      this.videoElement.playsInline = true; // Important for mobile
      
      // Create canvas for frame capture
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
      
      if (!this.ctx) {
        throw new Error('Failed to get 2D context from canvas');
      }
      
      // Set up video element
      this.videoElement.srcObject = videoStream;
      
      // Wait for video metadata to load and ensure video is playing
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Video metadata load timeout')), 5000);
        
        this.videoElement!.addEventListener('loadedmetadata', async () => {
          clearTimeout(timeout);
          
          try {
            // Ensure video starts playing
            await this.videoElement!.play();
            this.debugLog('✅ Video element is now playing:', {
              videoWidth: this.videoElement!.videoWidth,
              videoHeight: this.videoElement!.videoHeight,
              readyState: this.videoElement!.readyState,
              paused: this.videoElement!.paused
            });
            resolve();
          } catch (playError) {
            console.error('🔴 Failed to start video playback:', playError);
            reject(playError);
          }
        }, { once: true });
        
        this.videoElement!.addEventListener('error', (e) => {
          clearTimeout(timeout);
          reject(new Error(`Video load error: ${e}`));
        }, { once: true });
      });
      
      // Set canvas dimensions to match video
      this.canvas.width = this.videoElement.videoWidth;
      this.canvas.height = this.videoElement.videoHeight;
      
      // Update state (stream reference stored in coordinatorState)
      this.coordinatorState.isConnected = true;
      this.coordinatorState.frameWidth = this.videoElement.videoWidth;
      this.coordinatorState.frameHeight = this.videoElement.videoHeight;
      this.coordinatorState.sourceType = 'MediaStream';
      
      // ✅ CORRECT OffscreenCanvas approach: Transfer canvas to worker FIRST
      await this.transferOffscreenCanvasToWorker(this.videoElement.videoWidth, this.videoElement.videoHeight);
      
      // Start frame transfer loop
      this.startTransferLoop();
      
      this.debugLog('Video stream connected successfully (host-side coordinator)', {
        width: this.videoElement.videoWidth,
        height: this.videoElement.videoHeight
      });
      
    } catch (error) {
      console.error('Failed to set up video coordination:', error);
      this.coordinatorState.isConnected = false;
      this.disconnectVideoStream();
    }
  }

  /**
   * ✅ CORRECT: Transfer OffscreenCanvas to worker BEFORE getting context
   */
  private async transferOffscreenCanvasToWorker(width: number, height: number): Promise<void> {
    if (this.hasTransferredCanvas) {
      // Canvas already transferred, just update dimensions
      this.sendConfigurationToWorker({
        width: width,
        height: height,
        timestamp: performance.now()
      });
      return;
    }

    try {
      // Create OffscreenCanvas - NO CONTEXT on host side!
      const offscreenCanvas = new OffscreenCanvas(width, height);
      
      // Transfer immediately to worker (before any context creation)
      if (this.sendToWorker) {
        this.sendToWorker({
          type: 'video-canvas-setup',
          data: {
            offscreenCanvas,
            width,
            height,
            timestamp: performance.now()
          }
        }, [offscreenCanvas]); // ✅ Transfer ownership to worker
        
        this.hasTransferredCanvas = true;
        this.debugLog('✅ OffscreenCanvas transferred to worker (correct approach)', {
          width,
          height
        });
      }
    } catch (error) {
      console.error('Failed to transfer OffscreenCanvas to worker:', error);
      throw error;
    }
  }

  /**
   * Disconnect current video stream and clean up resources
   */
  private disconnectVideoStream(): void {
    // Stop transfer loop
    this.stopTransferLoop();
    
    // Clean up video element
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }
    
    // Clean up canvas
    if (this.canvas) {
      this.canvas = null;
      this.ctx = null;
    }
    
    // Clear stream reference
    // Stream cleanup handled by MediaStream track stopping
    
    // Update state
    this.coordinatorState.isConnected = false;
    this.coordinatorState.frameWidth = 0;
    this.coordinatorState.frameHeight = 0;
    this.coordinatorState.sourceType = '';
    this.hasTransferredCanvas = false;
    
    // Notify worker of disconnection
    this.sendDisconnectionToWorker();
    
    this.debugLog('Video stream disconnected (host-side coordinator)');
  }

  /**
   * Start the video frame transfer loop
   */
  private startTransferLoop(): void {
    if (this.isTransferRunning) return;
    
    this.isTransferRunning = true;
    this.lastTransferTime = performance.now();
    this.transferVideoFrame();
  }

  /**
   * Stop the video frame transfer loop
   */
  private stopTransferLoop(): void {
    this.isTransferRunning = false;
    if (this.transferLoopId !== null) {
      cancelAnimationFrame(this.transferLoopId);
      this.transferLoopId = null;
    }
  }

  /**
   * Transfer video frame to worker using ImageBitmap (for worker to draw on its OffscreenCanvas)
   */
  private transferVideoFrame(): void {
    if (!this.isTransferRunning || !this.videoElement || !this.canvas || !this.ctx) {
      if (!this.isTransferRunning) {
        this.debugLog('🔴 Transfer loop stopped');
      }
      return;
    }
    
    const currentTime = performance.now();
    const deltaTime = currentTime - this.lastTransferTime;
    
    // Only transfer frame if enough time has passed (FPS-based transfer)
    if (deltaTime >= this.transferInterval) {
      // Log occasionally to avoid spam (very rarely now that it's working)
      if (Math.random() < 0.01) { // Log ~1% of frames
        this.debugLog(`🔄 Transfer loop tick: ${deltaTime.toFixed(1)}ms since last frame`);
      }
      
      this.transferFrameToWorker().catch(error => {
        console.error('🔴 Error transferring video frame to worker:', error);
      });
      
      this.lastTransferTime = currentTime;
    }
    
    // Schedule next transfer check
    this.transferLoopId = requestAnimationFrame(() => this.transferVideoFrame());
  }

  /**
   * Async frame transfer using ImageBitmap (for worker to draw)
   */
  private async transferFrameToWorker(): Promise<void> {
    if (!this.videoElement || !this.canvas || !this.ctx) {
      console.warn('🔴 Frame transfer called but missing elements:', {
        hasVideo: !!this.videoElement,
        hasCanvas: !!this.canvas,
        hasCtx: !!this.ctx
      });
      return;
    }

    try {
      // Check if video is actually playing
      if (this.videoElement.readyState < 2) {
        console.warn('🔴 Video not ready for frame capture, readyState:', this.videoElement.readyState);
        return;
      }
      
      if (this.videoElement.videoWidth === 0 || this.videoElement.videoHeight === 0) {
        console.warn('🔴 Video has no dimensions:', {
          width: this.videoElement.videoWidth,
          height: this.videoElement.videoHeight
        });
        return;
      }
      
      // Draw current video frame to canvas
      this.ctx.drawImage(this.videoElement, 0, 0, this.canvas.width, this.canvas.height);
      
      // Create ImageBitmap from canvas (for worker to draw onto its OffscreenCanvas)
      const imageBitmap = await createImageBitmap(this.canvas);
      
      // Log occasionally to avoid spam (very rarely now that it's working)
      if (Math.random() < 0.01) { // Log ~1% of frames
        this.debugLog('✅ Frame captured and ImageBitmap created:', {
          videoDimensions: `${this.videoElement.videoWidth}x${this.videoElement.videoHeight}`,
          canvasDimensions: `${this.canvas.width}x${this.canvas.height}`,
          bitmapDimensions: `${imageBitmap.width}x${imageBitmap.height}`
        });
      }
      
      // Send ImageBitmap to worker (worker will draw it on its OffscreenCanvas)
      this.sendFrameToWorker(imageBitmap);
      
    } catch (error) {
      console.error('🔴 Failed to create ImageBitmap:', error);
    }
  }

  /**
   * Send ImageBitmap frame to worker (for worker to draw on its OffscreenCanvas)
   */
  private sendFrameToWorker(imageBitmap: ImageBitmap): void {
    if (this.sendToWorker) {
      this.sendToWorker({
        type: 'video-frame-update',
        data: {
          imageBitmap,
          timestamp: performance.now()
        }
      }, [imageBitmap]); // Transfer the ImageBitmap
    }
  }

  /**
   * Send configuration updates to worker
   */
  private sendConfigurationToWorker(config: {
    targetFrameRate?: number;
    cvConfig?: any;
    width?: number;
    height?: number;
    timestamp: number;
  }): void {
    if (this.sendToWorker) {
      this.sendToWorker({
        type: 'video-config-update',
        data: config
      });
    }
  }

  /**
   * Send disconnection notification to worker
   */
  private sendDisconnectionToWorker(): void {
    if (this.sendToWorker) {
      this.sendToWorker({
        type: 'video-config-update',
        data: {
          disconnect: true,
          timestamp: performance.now()
        }
      });
    }
  }

  /**
   * Handle image file as video source
   */
  public async setImageSource(imageFile: File): Promise<void> {
    try {
      // Create image element
      const img = new Image();
      const url = URL.createObjectURL(imageFile);
      
      // Load image
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image'));
        };
        img.src = url;
      });
      
      // Clean up existing connections
      this.disconnectVideoStream();
      
      // Update state for static image
      this.coordinatorState.isConnected = true;
      this.coordinatorState.frameWidth = img.width;
      this.coordinatorState.frameHeight = img.height;
      this.coordinatorState.sourceType = 'Image';
      
      // Transfer OffscreenCanvas to worker first
      await this.transferOffscreenCanvasToWorker(img.width, img.height);
      
      // Create ImageBitmap and send to worker for drawing
      const imageBitmap = await createImageBitmap(img);
      this.sendFrameToWorker(imageBitmap);
      
      this.debugLog('Image source set successfully (host-side coordinator)', {
        width: img.width,
        height: img.height
      });
      
    } catch (error) {
      console.error('Failed to set image source:', error);
      this.coordinatorState.isConnected = false;
      this.sendDisconnectionToWorker();
    }
  }

  /**
   * Reset all video coordinator state (called when destroying)
   */
  public resetVideoState(): void {
    this.disconnectVideoStream();
  }

  /**
   * Get current coordinator configuration
   */
  public getCoordinatorConfig(): { transferInterval: number } {
    return {
      transferInterval: this.transferInterval
    };
  }
} 